import { NgClass } from '@angular/common';
import {
  booleanAttribute,
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';

@Component({
  selector: 'app-select',
  imports: [FormsModule, ReactiveFormsModule, NgClass],
  templateUrl: './select.html',
  styleUrl: './select.scss',
})
export class Select {
  private elementRef = inject(ElementRef);
  @ViewChild('trigger') private triggerRef?: ElementRef<HTMLButtonElement>;

  @Input({ alias: 'control' }) control!: FormControl;
  @Input({ alias: 'inputClass' }) inputClass: string = '';
  @Input({ alias: 'labelClass' }) labelClass: string = '';
  @Input({ alias: 'label' }) label: string = '';
  @Input({ alias: 'secondaryLabel' }) secondaryLabel: string = '';
  @Input({ alias: 'errorMessage' }) errorMessage?: string;
  @Input({ alias: 'useDefaultError', transform: booleanAttribute }) useDefaultError: boolean = true;
  @Input({ alias: 'id' }) id: string = '';
  @Input({ alias: 'options' }) options: any[] = [];
  @Input({ alias: 'autocomplete' }) autocomplete: string = '';
  @Input({ alias: 'autofocus', transform: booleanAttribute }) autofocus: boolean = false;
  @Input({ alias: 'searchable', transform: booleanAttribute }) searchable: boolean = false;
  @Input('placeholder') placeholder: string = '';
  @Input('displayName') displayName = 'label';
  @Input('value') value = 'value';
  @Input('getObject') getObject = false;
  @Input('defaultValue') defaultValue: any = '';

  isOpen = false;
  searchTerm = '';
  dropdownPosition = { top: 0, left: 0, width: 0 };

  ngOnChanges(changes: SimpleChanges): void {
    try {
      if (this.useDefaultError && (changes['label'] || !this.errorMessage)) {
        this.errorMessage = `Invalid ${
          this.label || 'input'
        }. Please check your entries and try again.`;
      }
    } catch (error) {
      console.error('Error in Select ngOnChanges:', error);
    }
  }

  get selectedLabel(): string {
    const current = this.control?.value;
    if (current === null || current === undefined || current === '') return '';

    if (this.getObject) {
      return current?.[this.displayName] ?? '';
    }
    const match = this.options.find((option) => option[this.value] === current);
    return match ? match[this.displayName] : '';
  }

  get filteredOptions(): any[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return this.options;
    return this.options.filter((option) =>
      String(option[this.displayName] ?? '').toLowerCase().includes(term),
    );
  }

  toggleOpen(): void {
    if (this.control?.disabled) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.searchTerm = '';
      this.updateDropdownPosition();
    } else {
      this.control?.markAsTouched();
    }
  }

  private updateDropdownPosition(): void {
    const trigger = this.triggerRef?.nativeElement;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    this.dropdownPosition = {
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    };
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  onViewportChange(): void {
    if (this.isOpen) this.isOpen = false;
  }

  selectOption(option: any): void {
    this.control?.setValue(this.getObject ? option : option[this.value]);
    this.control?.markAsDirty();
    this.control?.markAsTouched();
    this.isOpen = false;
    this.searchTerm = '';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isOpen && !this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
      this.control?.markAsTouched();
    }
  }
}
